import { get, post } from "../../httpClients/clients/http.client";

const BASE_PATH = "/imports";

export type ImportJobStatus = "queued" | "running" | "success" | "failed";
export type ImportJobStepStatus = "pending" | "running" | "success" | "failed";

export interface ImportJobStepState {
  key: string;
  label: string;
  status: ImportJobStepStatus;
  progressPct: number;
  processedRows: number;
  totalRows?: number;
  rejectedCount: number;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  error?: string;
}

export interface ImportJobLogEntry {
  id: number;
  timestamp: string;
  stepKey: string;
  table: string;
  phase: string;
  line: number;
  errorName: string;
  errorMessage: string;
  row: unknown;
}

export interface ImportJobState {
  jobId: string;
  status: ImportJobStatus;
  progressPct: number;
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  error?: string;
  steps: ImportJobStepState[];
  logs: ImportJobLogEntry[];
}

export interface ImportJobStartResponse {
  jobId: string;
  status: ImportJobStatus;
  createdAt: string;
  steps: ImportJobStepState[];
  statusUrl: string;
}

export interface ImportRowRejectionRecord {
  id: number;
  jobId: string;
  stepKey: string;
  tableName: string;
  phase: string;
  lineNumber: number;
  rowPayload: unknown;
  errorName: string | null;
  errorMessage: string;
  createdAt: string;
}

export interface ImportRejectionPage {
  items: ImportRowRejectionRecord[];
  total: number;
  page: number;
  pageSize: number;
}

export const ImportApi = {
  startCountryInfoImportJob: (token: string, file: File) =>
    post<ImportJobStartResponse>(`${BASE_PATH}/run-country-info`, file, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  startCountryDemoImportJob: (token: string, file: File) =>
    post<ImportJobStartResponse>(`${BASE_PATH}/run-country-demo`, file, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  startT1DIndexImportJob: (token: string, file: File) =>
    post<ImportJobStartResponse>(`${BASE_PATH}/run-t1d-index`, file, {
      headers: { Authorization: `Bearer ${token}` },
    }),

  getActiveImportJob: (token: string, signal?: AbortSignal) =>
    get<{ job: ImportJobState | null }>(`${BASE_PATH}/active-job`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    }),

  getImportJobStatus: (token: string, jobId: string, signal?: AbortSignal) =>
    get<ImportJobState>(`${BASE_PATH}/jobs/${jobId}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    }),
  getImportJobRejections: (
    token: string,
    jobId: string,
    params: { page?: number; pageSize?: number; stepKey?: string } = {},
    signal?: AbortSignal
  ) => {
    const search = new URLSearchParams();
    if (params.page) search.set("page", String(params.page));
    if (params.pageSize) search.set("pageSize", String(params.pageSize));
    if (params.stepKey) search.set("stepKey", params.stepKey);
    const query = search.toString();
    return get<ImportRejectionPage>(
      `${BASE_PATH}/jobs/${jobId}/rejections${query ? `?${query}` : ""}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      }
    );
  },
  cancelImportJob: (token: string, jobId: string, reason?: string) =>
    post<ImportJobState>(`${BASE_PATH}/jobs/${jobId}/cancel`, reason ? { reason } : {}, {
      headers: { Authorization: `Bearer ${token}` },
    }),
};
